// Correcting a car sale that has already been made.
//
// A sold car used to be a dead card: whatever was typed at the moment of
// sale was what the lot said forever. But the buyer's name gets misheard and
// a price is agreed down after a test drive. All of that is normal, and none
// of it should need the superadmin.
//
// What is deliberately NOT here any more: the money's arrival. That lives in
// the receipts (CarMoneyModal) — one row per token, loan and closing payment
// — so this screen corrects only what the sale IS, never what was received.
//
// The one thing this screen makes visible is the consequence: on a
// commission car a changed price re-splits the owner's money, so the new
// split is shown while there is still a Cancel button.

import React, { useEffect, useState } from 'react';
import { Alert, Button } from '../common/index.js';
import {
  Field, Modal, NumberInput, errText, fmtMoney, inputCls,
} from './controls.jsx';
import { previewSplit } from './SellCarModal.jsx';
import { carsTeamApi } from '../../api/carsApi';

const EditCarSaleModal = ({ open, car, onClose, onSaved }) => {
  const sale = car?.sale;
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !sale) return;
    setForm({
      customer_name: sale.customer_name || '',
      customer_phone: sale.customer_phone || '',
      buyer_aadhaar: sale.buyer_aadhaar || '',
      sold_price: sale.sold_price != null ? String(Number(sale.sold_price)) : '',
      sold_at: sale.sold_at || '',
      notes: sale.notes || '',
    });
    setError('');
  }, [open, sale]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const consignment = car?.ownership === 'consignment';
  const preview = previewSplit(car, form.sold_price);
  const priceMoved = sale && Number(form.sold_price) !== Number(sale.sold_price);
  const collected = Number(sale?.collected || 0);

  const submit = async () => {
    setError('');
    const price = Number(form.sold_price);
    if (!price || price <= 0) { setError('What did it sell for?'); return; }
    if (price < collected) {
      setError(`${fmtMoney(collected)} is already collected — the price cannot go below it. `
        + 'Remove the wrong receipt first, in "The money".');
      return;
    }
    setBusy(true);
    try {
      await carsTeamApi.updateSale(car.id, {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        buyer_aadhaar: form.buyer_aadhaar.trim(),
        sold_price: form.sold_price,
        sold_at: form.sold_at,
        notes: form.notes,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(errText(e, 'Could not save the sale.'));
    } finally {
      setBusy(false);
    }
  };

  if (!sale) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Correct this sale"
      subtitle={`${car.display_name} — fix anything that was taken down wrong.`}
    >
      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Buyer">
          <input className={inputCls} value={form.customer_name || ''}
            onChange={(e) => set('customer_name')(e.target.value)} placeholder="Who bought it" />
        </Field>
        <Field label="Phone">
          <input className={inputCls} value={form.customer_phone || ''}
            onChange={(e) => set('customer_phone')(e.target.value)}
            placeholder="10-digit mobile number" />
        </Field>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Sold for" required>
          <NumberInput value={form.sold_price} onChange={set('sold_price')} />
        </Field>
        <Field label="Sold on">
          <input type="date" className={inputCls} value={form.sold_at || ''}
            onChange={(e) => set('sold_at')(e.target.value)} />
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Buyer's Aadhaar" hint="For the RC transfer — optional">
          <input className={inputCls} value={form.buyer_aadhaar || ''}
            onChange={(e) => set('buyer_aadhaar')(e.target.value)}
            placeholder="12 digits, if they share it" inputMode="numeric" />
        </Field>
      </div>

      {/* Raising the price on a part-paid deal reopens a balance — say so. */}
      {priceMoved && Number(form.sold_price) > collected && collected > 0 && (
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700">
          {fmtMoney(Number(form.sold_price) - collected)} will be left to collect —
          the car stays BOOKED until it arrives.
        </p>
      )}

      {/* A changed price moves the owner's money on a commission car. */}
      {consignment && preview && priceMoved && (
        <div className="mt-3 rounded-2xl border border-violet-200 bg-violet-50 p-3">
          <p className="text-[11px] font-black uppercase tracking-wide text-violet-700">
            The split changes with this price
          </p>
          <p className="mt-1 text-sm font-bold text-violet-900">
            {car.owner_name} gets {fmtMoney(preview.owner)} · CAP keeps {fmtMoney(preview.commission)}
          </p>
        </div>
      )}

      <div className="mt-3">
        <Field label="Notes">
          <textarea rows={2} className={inputCls} value={form.notes || ''}
            onChange={(e) => set('notes')(e.target.value)} />
        </Field>
      </div>

      <p className="mt-3 text-[11px] text-slate-400">
        Wrong payment? The receipts live in <span className="font-bold">The money</span> on
        the car's card — remove the wrong one there and enter it again.
      </p>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>Save sale</Button>
      </div>
    </Modal>
  );
};

export default EditCarSaleModal;
